import { HttpHeaders, HttpParams, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';

import { CharonSessionService } from '../../../http/src/charon.http.session.service';
import { IHateoas } from '../hateoas/hateoas.interface';
import { HateoasService } from '../hateoas/hateoas.service';
import { FullHttpOptions, IHttpOptions, IInitializeParam, ResultObservable } from '../interfaces/httpOptions.interface';
import { ILink } from '../interfaces/link.interface';


@Injectable()
export abstract class CharonService {

  constructor (
    protected hateoas: HateoasService,
    protected charonSession: CharonSessionService,
  ) {}

  abstract initialize<ResultData extends HttpResponse<any>, HttpOptions extends FullHttpOptions>(
    parameters?: IInitializeParam): ResultObservable<ResultData, HttpOptions>;

  abstract config(options: IHateoas): void;

  abstract request<ResultData, HttpOptions extends FullHttpOptions>(
    first: string | HttpRequest<any>,
    url?: string,
    options?: IHttpOptions & { body?: any; }): ResultObservable<ResultData, HttpOptions>;

  abstract get(url: string, options?: IHttpOptions): Observable<any>;

  abstract post(url: string, body: any, options: IHttpOptions): Observable<any>;

  abstract put(url: string, body: any, options: IHttpOptions): Observable<any>;

  abstract delete(url: string, options: IHttpOptions): Observable<any>;

  abstract setCryptography(hasCrypto: boolean): void;

  abstract saveEntrypointData(initializeParam: IInitializeParam, event: HttpResponse<any>): void;

  /**super.
  * Método que retorna todas as relations encontradas em memória.
  *
  * Method that returns all relations found on memory.
  */
  getRelations(): ILink[] {
    return this.hateoas.getRelations();
  }

  /**
   * Método que retorna a instância de referência do HateoasService baseado nas informações de entrypoint.
   *
   * Method that returns the instance of HateoasService based on entrypoint information.
   *
   * @param entryPoint
   * @internal
   */
  protected getHateoasRef(entryPoint?: string): HateoasService {
    const hasEntrypoint = entryPoint && !this.hateoas.entryPoint;
    if (hasEntrypoint) {
      throwError(() => new Error('URL base não configurada.'));
    }
    const validateEntryPoint = entryPoint && entryPoint.startsWith('http') && entryPoint !== this.hateoas.entryPoint;
    if (validateEntryPoint) {
      const hateoas = new HateoasService();
      hateoas.setConfig({ entryPoint });
      return hateoas;
    }

    return this.hateoas;
  }

  /**
   * Método que inclui HttpHeaders adicionais aos HttpHeaders em memória.
   *
   * Method that includes additional HttpHeaders in memory.
   *
   * @internal
   * @param additionalHeaders
   */
  protected addAdditionalHeaders(additionalHeaders?: HttpHeaders): HttpHeaders {
    const hateoasHeaders = this.hateoas.httpOptions?.headers as HttpHeaders ?? new HttpHeaders();

    if (!additionalHeaders) {
      return hateoasHeaders;
    }

    return additionalHeaders.keys().reduce(
      // tslint:disable-next-line: no-non-null-assertion
      (headers, key) => headers.set(key, additionalHeaders.get(key)!),
      hateoasHeaders,
    );

  }

  /**
   * Método que inclui HttpParams adicionais aos HttpParams em memória.
   *
   * Method that includes additional HttpParams in memory.
   *
   * @internal
   * @param additionalParams
   */
  protected addAdditionalParams(additionalParams?: unknown | HttpParams): unknown | HttpParams {
    const hateoasParams = this.hateoas.httpOptions?.params as HttpParams ?? new HttpParams();

    if (!additionalParams || typeof additionalParams !== "object") {
      return hateoasParams;
    }

    if (additionalParams instanceof HttpParams) {
      return additionalParams.keys().reduce(
        // tslint:disable-next-line: no-non-null-assertion
        (params, key) => params.appendAll({ [key]: additionalParams.getAll(key)! }),
        hateoasParams,
      );
    }

    return { ...additionalParams, ...hateoasParams };
  }
}
